import { z } from 'zod'
import type { StatusTone } from '@/design-system'
import { exclusionLabel } from './smsCampaigns'
import { consentRowSchema } from './smsConsents'
import { journalRowSchema, journalStates } from './smsJournal'
import { reasonLabel } from './smsReasons'
import { formatPhone, smsModes } from './smsSettings'

/**
 * Onglet « Alertes SMS » de la fiche Contrat (`contract_sms_overview_v1`) : tout ce qui touche les SMS d'un contrat.
 * Le serveur calcule chaque raison avec les fonctions du module SMS ; rien n'est recalculé ici. Un bloc réservé à
 * une permission que la session n'a pas vaut null.
 */

const lookupSchema = z
  .object({
    e164: z.string(),
    status: z.string(),
    phoneType: z.string().nullable(),
    carrier: z.string().nullable(),
    smsCapable: z.boolean().nullable(),
    lookedUpAt: z.string().nullable(),
  })
  .nullable()

export const contractSmsPhoneSchema = z.object({
  kind: z.enum(['MOBILE', 'PHONE', 'LINKED']),
  raw: z.string().nullable(),
  e164: z.string().nullable(),
  contactId: z.string().nullable(),
  lookup: lookupSchema,
  administrativeReason: z.string().nullable(),
  campaignExclusion: z.string().nullable(),
  conversationId: z.string().nullable(),
  consent: consentRowSchema.nullable(),
})
export type ContractSmsPhone = z.infer<typeof contractSmsPhoneSchema>

export const contractSmsOverviewSchema = z.object({
  module: z.object({ mode: z.enum(smsModes), sendingPaused: z.boolean() }).nullable(),
  phones: z.array(contractSmsPhoneSchema),
  consentsVisible: z.boolean(),
  routes: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      season: z.string(),
      addresses: z.array(z.string()).nullable(),
    }),
  ),
  campaigns: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        announcedDate: z.string(),
        createdAt: z.string(),
        cancelledAt: z.string().nullable(),
        recipients: z.array(
          z.object({
            phone: z.string().nullable(),
            exclusion: z.string().nullable(),
            state: z.enum(journalStates).nullable(),
          }),
        ),
      }),
    )
    .nullable(),
  messages: z.array(journalRowSchema).nullable(),
})
export type ContractSmsOverview = z.infer<typeof contractSmsOverviewSchema>

const kindLabels: Record<ContractSmsPhone['kind'], string> = {
  MOBILE: 'Mobile',
  PHONE: 'Téléphone',
  LINKED: 'Rattaché au dossier',
}

export function contractSmsPhoneTitle(phone: ContractSmsPhone): string {
  return `${kindLabels[phone.kind]} · ${phone.e164 ? formatPhone(phone.e164) : (phone.raw ?? '—')}`
}

/** État du module en une étiquette. La suspension prime, comme dans l'en-tête du module SMS. */
export function contractSmsModuleState(module: ContractSmsOverview['module']): {
  label: string
  tone: StatusTone
} {
  if (!module) return { label: 'Non configuré', tone: 'neutralLight' }
  if (module.sendingPaused) return { label: 'Envois suspendus', tone: 'danger' }
  if (module.mode === 'PRODUCTION') return { label: 'Production', tone: 'success' }
  if (module.mode === 'TEST') return { label: 'Pilote (numéros d’équipe seulement)', tone: 'pending' }
  if (module.mode === 'SIMULATION') return { label: 'Simulation (aucun SMS transmis)', tone: 'info' }
  return { label: 'Envois désactivés', tone: 'neutral' }
}

const targetLabels: Record<string, string> = {
  CONTRACT_NOT_TARGETED: 'contrat écarté des campagnes (statut ou saison)',
  NUMBER_NOT_TARGETED: 'numéro absent de la fiche client : les campagnes ne le lisent pas',
}

/**
 * Ce qu'une campagne d'alertes sur les routes du contrat ferait aujourd'hui pour ce numéro. Null : le contrat n'est
 * sur aucune route, ou le numéro est illisible.
 */
export function campaignOutcome(phone: ContractSmsPhone): { label: string; tone: StatusTone } | null {
  const code = phone.campaignExclusion
  if (code === null) return null
  if (code === 'NONE') return { label: 'Recevrait l’alerte', tone: 'success' }
  return { label: `Exclu : ${targetLabels[code] ?? exclusionLabel(code).toLowerCase()}`, tone: 'warning' }
}

/** Avis administratifs (contrat, facture, paiement) : possible, ou la raison du refus. */
export function administrativeOutcome(phone: ContractSmsPhone): { label: string; tone: StatusTone } {
  return phone.administrativeReason === null
    ? { label: 'Possible', tone: 'success' }
    : { label: `Impossible : ${reasonLabel(phone.administrativeReason)}`, tone: 'warning' }
}

/**
 * Case « Alertes SMS » de la fiche Client : ce qu'une alerte de passage ferait aujourd'hui pour le contrat courant.
 * Reprend `campaignOutcome` numéro par numéro ; l'état du module est rappelé quand il n'est pas en production.
 */
export function contractSmsAlertSummary(overview: ContractSmsOverview): {
  label: string
  tone: StatusTone
  note: string
} {
  const module = contractSmsModuleState(overview.module)
  const moduleNote = module.label === 'Production' ? '' : ` · ${module.label}`
  const receiving = overview.phones.filter((phone) => phone.campaignExclusion === 'NONE')
  if (receiving.length > 0) {
    const numbers = receiving.map((phone) => (phone.e164 ? formatPhone(phone.e164) : (phone.raw ?? '—')))
    return { label: 'Reçoit les alertes', tone: 'success', note: `${numbers.join(', ')}${moduleNote}` }
  }
  const excluded = overview.phones.map(campaignOutcome).find((outcome) => outcome !== null)
  if (excluded) return { label: 'Aucune alerte', tone: 'warning', note: `${excluded.label}${moduleNote}` }
  if (overview.routes.length === 0) {
    return { label: 'Hors route', tone: 'neutral', note: `Le contrat n’est sur aucune route${moduleNote}` }
  }
  return { label: 'Aucune alerte', tone: 'warning', note: `Aucun numéro lisible au dossier${moduleNote}` }
}
